import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux';
import { Button, Card } from 'react-bootstrap';
import axios from 'axios';

const socialMediaPlatforms = [
  { name: 'Facebook',  icon: <img src="assets/img/social/facebook-ico.png"  alt="Facebook Icon"  style={{ width: 20, height: 20 }} />, key: 'facebook'  },
  { name: 'Instagram', icon: <img src="assets/img/social/instagram-ico.svg" alt="Instagram Icon" style={{ width: 20, height: 20 }} />, key: 'instagram' },
  { name: 'YouTube',   icon: <img src="assets/img/social/youtube-ico.svg"   alt="YouTube Icon"   style={{ width: 20, height: 20 }} />, key: 'youtube'  },
  { name: 'TikTok',    icon: <img src="assets/img/social/tiktok-ico.svg"    alt="TikTok Icon"    style={{ width: 20, height: 20 }} />, key: 'tiktok'   }
];

const SocialConnect = () => {
  const [connectedAccounts, setConnectedAccounts] = useState({});
  const [busy, setBusy] = useState({}); // { facebook: bool, instagram: bool, youtube: bool }

  const l = useSelector((state) => state.language || "en");
  const t = useSelector((state) => state.translation || "en");

  // Kick off OAuth (Facebook)
  const connectFacebook = async () => {
    try {
      setBusy(b => ({ ...b, facebook: true }));
      const returnTo = window.location.href;
      const { data } = await axios.get(`/getfacebookurl`, { params: { returnTo } });
      if (data?.message) window.location.assign(data.message);
    } finally {
      setBusy(b => ({ ...b, facebook: false }));
    }
  };

  // Kick off OAuth (Instagram)
  const connectInstagram = async () => {
    try {
      setBusy(b => ({ ...b, instagram: true }));
      const returnTo = window.location.href;
      const { data } = await axios.get(`/getinstagramurl`, { params: { returnTo } });
      if (data?.message) window.location.assign(data.message);
    } finally {
      setBusy(b => ({ ...b, instagram: false }));
    }
  };

  // Kick off OAuth (YouTube)
  const connectYouTube = async () => {
    try {
      setBusy(b => ({ ...b, youtube: true }));
      const returnTo = window.location.href;
      const { data } = await axios.get(`/getyoutubeurl`, { params: { returnTo } });
      if (data?.message) window.location.assign(data.message);
    } finally {
      setBusy(b => ({ ...b, youtube: false }));
    }
  };

  // Kick off OAuth (TikTok)
  const connectTikTok = async () => {
    try {
      setBusy(b => ({ ...b, tiktok: true }));
      const returnTo = window.location.href;
      // Make sure your server exposes this route and returns { message: "https://tiktok-auth..." }
      const { data } = await axios.get(`/gettiktokurl`, { params: { returnTo } });
      if (data?.message) window.location.assign(data.message);
    } finally {
      setBusy(b => ({ ...b, tiktok: false }));
    }
  };

  // Disconnect handler (shared)
  const disconnectSocial = async (providerKey) => {
    const confirmed = window.confirm(`Disconnect ${providerKey.charAt(0).toUpperCase() + providerKey.slice(1)}?`);
    if (!confirmed) return;

    try {
      setBusy(b => ({ ...b, [providerKey]: true }));
      await axios.post(`/disconnectsocial`, { provider: providerKey });
      // Optimistic UI
      setConnectedAccounts(prev => ({ ...prev, [providerKey]: false }));
    } catch (err) {
      console.error('Disconnect error', err);
      alert('Failed to disconnect. Please try again.');
    } finally {
      setBusy(b => ({ ...b, [providerKey]: false }));
    }
  };

  // Initial fetch to see what’s connected
  useEffect(() => {
    (async () => {
      try {
        const result = await axios.get(`getsocialmedia/`);
        const rec = result?.data?.socialMedias || [];
        const fbRecord = rec.find(x => x.provider === "facebook"  && x.status === "true" && x.userToken);
        const igRecord = rec.find(x => x.provider === "instagram" && x.status === "true" && x.userToken);
        const ytRecord = rec.find(x => x.provider === "youtube"   && x.status === "true" && (x.userToken || x.userRefreshToken));
        const ttRecord = rec.find(x => x.provider === "tiktok"    && x.status === "true" && (x.userToken || x.userRefreshToken));

        setConnectedAccounts({
          facebook:  !!fbRecord,
          instagram: !!igRecord,
          youtube:   !!ytRecord,
          tiktok:    !!ttRecord,
        });
      } catch (e) {
        console.error('Fetch social status failed', e);
      }
    })();
  }, []);

  const permissions = useSelector(state => state.permissions) || [];
  const roleTitle = useSelector(state => state.roleTitle) || '';
  const roleTitleAdmin = useSelector(state => state.roleTitleAdmin) || '';
  const canView =
    roleTitle === roleTitleAdmin ||
    permissions.some(p => p.Module.toLowerCase() === 'brands' && p.Permission.toLowerCase() === 'view');

  if (!canView) {
    return (
      <div className={`${l === 'ar' ? 'page-wrapper-rtl' : 'page-wrapper'}`}>
        <div className="content">
          <p>{t?.[l]?.["no_permission"] || "You do not have permission to view this page."}</p>
        </div>
      </div>
    );
  }

  
  const handlers = {
    facebook: connectFacebook,
    instagram: connectInstagram,
    youtube: connectYouTube,
    tiktok: connectTikTok,
  };

  return (
    <div className={`${l === 'ar' ? 'page-wrapper-rtl' : 'page-wrapper'}`}>
      <div className="content">
        <Card className="p-3">
          <Card.Body>
            <Card.Title>Connect Socials</Card.Title>
            <Card.Text>Connect your social media accounts to allow Marky to post on your behalf.</Card.Text>
            <div className="list-group">
              {socialMediaPlatforms.map(({ name, icon, key }) => {
                const connectHandler = handlers[key];
                const isConnected = !!connectedAccounts[key];
                const isBusy = !!busy[key];

                return (
                  <div key={key} className="d-flex justify-content-between align-items-center border-bottom p-2">
                    <div className="d-flex align-items-center">
                      <span className="me-2" style={{ fontSize: 20 }}>{icon}</span>
                      <span>{name}</span>
                    </div>

                    {isConnected ? (
                      <div className="d-flex justify-content-end">
                        <Button variant="success" disabled className="me-2">
                          Connected
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => disconnectSocial(key)}
                          disabled={isBusy}
                          title="Disconnect this account"
                        >
                          {isBusy ? 'Disconnecting…' : 'Disconnect'}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="primary"
                        onClick={connectHandler}
                        disabled={isBusy}
                      >
                        {isBusy ? 'Connecting…' : 'Connect'}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default SocialConnect;
